import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Banner, FormShell } from "@/components/auth/FormShell";
import { Icon } from "@/components/ui/Icon/Icon";
import { useAuth } from "@/lib/AuthContext";
import {
  clearPkceState,
  exchangeGoogleCallback,
  readPkceNext,
  type AuthError,
} from "@/lib/auth";

/**
 * OAuth2 callback for Cognito Hosted UI federation. We land here from
 * /oauth2/authorize with one of:
 *
 *   - ?code=…&state=…                       → success path; exchange for tokens.
 *   - ?error=access_denied&error_description → IdP refused, or Cognito's
 *     /oauth2/authorize rejected a disabled user up-front.
 *
 * We inspect ?error FIRST. If we tried the token exchange before noticing
 * the error param, Cognito would 400 with a confusing message and the user
 * would never see the real reason (waitlist, account collision, etc.).
 *
 * The token exchange itself can ALSO surface a disabled-user error — that
 * happens to first-time federated users, because PostConfirmation calls
 * AdminDisableUser before Cognito gets to /oauth2/token. The exchange
 * helper maps that to a `waitlist_pending` AuthError so we route to the
 * same waitlist banner instead of showing a generic failure.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  // Stable function identity — useCallback([]) in AuthContext — so this
  // doesn't cause the effect to re-fire when AuthContext re-renders.
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev. Without a guard we'd POST
  // /oauth2/token twice and the second call would 400 ("invalid_grant" —
  // the code is one-shot). The ref pins us to a single exchange.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const next = readPkceNext();

    const oauthError = params.get("error");
    if (oauthError) {
      const description = params.get("error_description") ?? oauthError;
      clearPkceState();
      if (/disab/i.test(description)) {
        navigate("/signin?reason=waitlist", { replace: true });
      } else {
        navigate(
          `/signin?error=${encodeURIComponent(description)}`,
          { replace: true },
        );
      }
      return;
    }

    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      clearPkceState();
      navigate(
        "/signin?error=Sign-in+returned+no+code",
        { replace: true },
      );
      return;
    }

    void (async () => {
      try {
        await exchangeGoogleCallback(code, state);
        refresh();
        navigate(next, { replace: true });
      } catch (err) {
        // exchangeGoogleCallback maps disabled-user / waitlist responses
        // to a typed AuthError; route those to the waitlist banner so
        // first-time federated users see the same message native signups
        // do, not a generic "Sign-in failed" message.
        const authErr = err as AuthError;
        if (authErr.kind === "waitlist_pending") {
          navigate("/signin?reason=waitlist", { replace: true });
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      }
    })();
  }, [navigate, refresh]);

  return (
    <AuthLayout view="signin">
      <FormShell
        title={error ? "Sign-in didn’t complete" : "Finishing sign-in…"}
        subtitle={
          error
            ? "We couldn’t complete the SSO sign-in. Try again or use email + password."
            : "Hold tight — we’re exchanging the code with Cognito."
        }
      >
        {error ? (
          <Banner variant="danger" icon={<Icon name="info" size={14} />}>
            {error}
          </Banner>
        ) : (
          <Banner variant="info" icon={<Icon name="refresh" size={14} />}>
            One moment — completing your sign-in.
          </Banner>
        )}
      </FormShell>
    </AuthLayout>
  );
}
